import React from "react";
import { FaRupeeSign, FaBoxes, FaUsers, FaExclamationTriangle } from 'react-icons/fa';
import { MdInventory, MdPeople, MdWarning } from 'react-icons/md';
import { IoIosPeople, IoMdAlert } from 'react-icons/io';
import { BiMoney, BiPackage } from 'react-icons/bi';

const BranchView = () => {
  // Sample branch data
  const branchData = {
    branchID: "BR001",
    branchName: "Main Branch Pharmacy",
    location: "Central District, Mumbai",
    pharmacist: "Dr. Rajesh Kumar",
    contact: "+91 98765 43210",
    email: "mainbranch@pharmacy.com",
    status: "Active",
    address: "Shop No. 15, Ground Floor, Central Plaza Building, Linking Road, Mumbai - 400050",
    establishedDate: "2018-03-15",
    licenseNumber: "MH-PH-2018-001234",
    workingHours: "9:00 AM - 9:00 PM",
    staffCount: 8,
    totalSales: "₹45,67,890",
    inventoryValue: "₹12,34,567",
    lowStockItems: 5,
    expiringSoon: 12
  };


  const stats = [
    { 
      label: "Today's Sales", 
      value: "₹25,450", 
      color: "text-green-600", 
      bgColor: "bg-green-50",
      icon: <FaRupeeSign className="text-green-600" size={24} />
    },
    { 
      label: "Inventory Value", 
      value: branchData.inventoryValue, 
      color: "text-blue-600", 
      bgColor: "bg-blue-50",
      icon: <FaBoxes className="text-blue-600" size={24} />
    },
    { 
      label: "Staff Members", 
      value: branchData.staffCount, 
      color: "text-purple-600", 
      bgColor: "bg-purple-50",
      icon: <FaUsers className="text-purple-600" size={24} />
    },
    { 
      label: "Low Stock Alerts", 
      value: branchData.lowStockItems, 
      color: "text-red-600", 
      bgColor: "bg-red-50",
      icon: <FaExclamationTriangle className="text-red-600" size={24} />
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="px-8 py-6 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">BR</span>
                  </div>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{branchData.branchName}</h1>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-gray-600">{branchData.branchID}</span>
                    <span className="text-gray-400">•</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      branchData.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {branchData.status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button className="gradient-button">
                  Edit Branch
                </button>
                <button className="gradient-dark-button">
                  View Inventory
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6">
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${stat.bgColor} mb-4`}>
                {stat.icon}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-gray-600 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Branch Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Branch Information</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Branch ID</label>
                      <p className="text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded-md">{branchData.branchID}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Branch Name</label>
                      <p className="text-gray-900">{branchData.branchName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Location</label>
                      <p className="text-gray-900">{branchData.location}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Complete Address</label>
                      <p className="text-gray-900 text-sm leading-relaxed">{branchData.address}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Pharmacist In-Charge</label>
                      <p className="text-gray-900">{branchData.pharmacist}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Contact Number</label>
                      <p className="text-gray-900 font-mono">{branchData.contact}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
                      <p className="text-gray-900">{branchData.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Working Hours</label>
                      <p className="text-gray-900">{branchData.workingHours}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Operational Details */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Operational Details</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Established Date</label>
                    <p className="text-gray-900">{new Date(branchData.establishedDate).toLocaleDateString('en-IN', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">License Number</label>
                    <p className="text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded-md text-sm">{branchData.licenseNumber}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Total Staff</label>
                    <p className="text-gray-900 text-lg font-semibold">{branchData.staffCount} Members</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
              </div>
              <div className="p-6 space-y-3">
                <button className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-3 rounded-lg text-left font-medium transition-colors">
                  View Inventory
                </button>
                <button className="w-full bg-green-50 hover:bg-green-100 text-green-700 px-4 py-3 rounded-lg text-left font-medium transition-colors">
                  Sales Report
                </button>
                <button className="w-full bg-orange-50 hover:bg-orange-100 text-orange-700 px-4 py-3 rounded-lg text-left font-medium transition-colors">
                  Transfer Requests
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm text-gray-900">New stock received</p>
                      <p className="text-xs text-gray-500">Paracetamol 500mg - 2 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm text-gray-900">Transfer request approved</p>
                      <p className="text-xs text-gray-500">To City Center Branch - 4 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm text-gray-900">Low stock alert</p>
                      <p className="text-xs text-gray-500">Amoxicillin 250mg - 6 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm text-gray-900">Staff check-in</p>
                      <p className="text-xs text-gray-500">Dr. Priya Sharma - 8 hours ago</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                    View all activity →
                  </button>
                </div>
              </div>
            </div>

            {/* Alerts */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Alerts & Notifications</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span className="text-red-800 font-medium text-sm">{branchData.lowStockItems} Low Stock Items</span>
                    </div>
                    <p className="text-red-700 text-xs mt-1">Immediate restocking required</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <span className="text-yellow-800 font-medium text-sm">{branchData.expiringSoon} Items Expiring Soon</span>
                    </div>
                    <p className="text-yellow-700 text-xs mt-1">Within next 30 days</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-green-800 font-medium text-sm">License Valid</span>
                    </div>
                    <p className="text-green-700 text-xs mt-1">Expires on March 2026</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchView;
